import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  BatteryCharging,
  Zap,
  Gauge,
  Shield,
  Wrench,
  Clock,
  Globe,
  Car,
  Sparkles,
} from "lucide-react";
import { MODELS, getFeaturedModels } from "../data/models";
import { SALES, DEALER, SITE, buildWhatsAppLink } from "../data/constants";
import SEO from "../components/SEO";
import SchemaMarkup from "../components/SchemaMarkup";
import PageSeoOverride from "../components/PageSeoOverride";
import SalesIdentity from "../components/SalesIdentity";
import Handovers from "../components/Handovers";
import HeroSlider from "../components/HeroSlider";
import DealerLocation from "../components/DealerLocation";
import FAQ from "../components/FAQ";
import Picture from "../components/Picture";
import { DEFAULT_FAQ } from "../data/models";
import { useContent, applyOverride } from "../lib/ContentContext";
import { useSettings, buildWaLink } from "../lib/SettingsContext";
import { trackWhatsAppLead } from "../lib/AnalyticsInjector";

// Icon name -> lucide component map (admin sets icon name in /admin/content)
const VALUE_ICON_MAP = {
  battery: BatteryCharging,
  zap: Zap,
  gauge: Gauge,
  shield: Shield,
  wrench: Wrench,
  clock: Clock,
  globe: Globe,
  car: Car,
  sparkles: Sparkles,
};

const baseFeatured = getFeaturedModels();
const baseOthers = MODELS.filter((m) => !m.featured);

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "AutoDealer",
  "@id": `${SITE.domain}/#dealer`,
  name: DEALER.name,
  image: DEALER.image,
  telephone: `+${SALES.phoneIntl}`,
  priceRange: "Rp 195.000.000 - Rp 1.500.000.000",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Jl. Alternatif Cibubur No.41, RT.002/RW.002, Jatikarya",
    addressLocality: DEALER.city,
    addressRegion: DEALER.region,
    postalCode: DEALER.postalCode,
    addressCountry: DEALER.country,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: DEALER.latitude,
    longitude: DEALER.longitude,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "08:30",
      closes: "17:30",
    },
  ],
  url: SITE.domain,
  sameAs: [DEALER.mapUrl],
  employee: {
    "@type": "Person",
    name: SALES.name,
    jobTitle: SALES.title,
    image: SALES.photo,
    telephone: `+${SALES.phoneIntl}`,
  },
};

export default function Home() {
  const { t, overrides, loaded } = useContent();
  const { settings } = useSettings();
  const featured = baseFeatured.map((m) => applyOverride(m, overrides[m.slug]));
  const others = baseOthers.map((m) => applyOverride(m, overrides[m.slug]));
  return (
    <>
      <SEO
        title="Dealer BYD Cibubur - BYD M6, Atto 1 & Lineup EV Premium | Haka Auto"
        description={`Dealer Resmi BYD Haka Auto Cibubur. Konsultasi langsung dengan ${SALES.name} untuk BYD M6, Atto 1, Seal, Atto 3, Dolphin, Sealion 7 & Denza D9. Hitungan kredit terbaik, test drive gratis.`}
        canonical={`${SITE.domain}/`}
        ogImage={featured[0].hero}
      />
      <SchemaMarkup id="home-local" schema={localBusinessSchema} />
      <PageSeoOverride pageKey="home" />

      {/* HERO */}
      <section className="relative" data-testid="hero-section">
        <HeroSlider />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-12 lg:pt-16 pb-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.25em] text-[#6E6E73] fade-up">
                {t("home.eyebrow", "Authorized BYD Dealer · Cibubur")}
              </p>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-medium tracking-tight text-[#0F0F0F] leading-[1.02] fade-up fade-up-delay-1 whitespace-pre-line">
                {t("home.heading", "Kendaraan Listrik\nuntuk keluarga yang\nberpikir panjang.")}
              </h1>
            </div>
            <p className="max-w-sm text-base text-[#6E6E73] leading-relaxed fade-up fade-up-delay-2">
              {t("home.subheading", "Lineup BYD resmi dari hatchback kompak hingga MPV mewah.")}
            </p>
          </div>
        </div>

        {/* Priority bento: featured[0] first (Atto 1) + featured[1] (M6) */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
            <Link
              to={`/model/${featured[0].slug}`}
              className="lg:col-span-8 group relative aspect-[16/10] lg:aspect-[16/11] rounded-3xl overflow-hidden bg-[#0F0F0F] fade-up fade-up-delay-2"
              data-testid={`hero-${featured[0].slug}-card`}
            >
              {loaded ? (
                <Picture
                  key={featured[0].hero}
                  src={featured[0].hero}
                  alt={`${featured[0].name} - ${featured[0].tagline}`}
                  className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-[1400ms] ease-out"
                  width={1600}
                  height={1100}
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 bg-[#1F1F22] animate-pulse" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute top-6 left-6 flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.2em] bg-white text-[#0F0F0F] px-3 py-1.5 rounded-full font-medium">
                  Terlaris
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-10 text-white">
                <p className="text-xs uppercase tracking-[0.2em] text-white/70 mb-2">
                  {featured[0].category}
                </p>
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <h2 className="text-3xl lg:text-5xl font-medium tracking-tight">
                      {featured[0].name}
                    </h2>
                    <p className="text-white/80 text-sm lg:text-base mt-1">
                      {featured[0].tagline}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-white/70">Harga</p>
                    <p className="text-base lg:text-lg font-medium">
                      {featured[0].priceLabel}
                    </p>
                  </div>
                </div>
                <div className="mt-5 hidden sm:flex gap-3">
                  {featured[0].highlights.slice(0, 3).map((h) => (
                    <div
                      key={h.label}
                      className="flex-1 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 px-3 py-2"
                    >
                      <p className="text-[10px] text-white/60 uppercase tracking-wider">
                        {h.label}
                      </p>
                      <p className="text-xs font-medium mt-0.5">{h.value}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium">
                  Pelajari {featured[0].name}
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </div>
            </Link>

            <Link
              to={`/model/${featured[1].slug}`}
              className="lg:col-span-4 group relative aspect-[4/5] lg:aspect-auto rounded-3xl overflow-hidden bg-[#F5F5F7] fade-up fade-up-delay-3"
              data-testid={`hero-${featured[1].slug}-card`}
            >
              {loaded ? (
                <Picture
                  key={featured[1].hero}
                  src={featured[1].hero}
                  alt={`${featured[1].name} - ${featured[1].tagline}`}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1400ms] ease-out"
                  width={800}
                  height={1000}
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 bg-[#EAEAEC] animate-pulse" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute top-6 left-6">
                <span className="text-[10px] uppercase tracking-[0.2em] bg-white text-[#0F0F0F] px-3 py-1.5 rounded-full font-medium">
                  Terlaris
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 text-white">
                <p className="text-xs uppercase tracking-[0.2em] text-white/70 mb-1">
                  {featured[1].category}
                </p>
                <h2 className="text-2xl lg:text-3xl font-medium tracking-tight">
                  {featured[1].name}
                </h2>
                <p className="text-white/80 text-xs lg:text-sm mt-1">
                  {featured[1].tagline}
                </p>
                <p className="mt-3 text-base lg:text-lg font-medium">
                  {featured[1].priceLabel}
                </p>
                <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium">
                  Lihat {featured[1].name}
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Value props strip */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-1 md:grid-cols-3 gap-10 border-b border-[#E5E5EA]">
        {[
          {
            iconKey: t("home.value_1_icon", "battery"),
            iconUrl: t("home.value_1_icon_url", ""),
            title: t("home.value_1_title", "Blade Battery Tahan Lama"),
            desc: t("home.value_1_desc", "Teknologi LFP tahan tusuk paku."),
          },
          {
            iconKey: t("home.value_2_icon", "gauge"),
            iconUrl: t("home.value_2_icon_url", ""),
            title: t("home.value_2_title", "Daya Jelajah Panjang"),
            desc: t("home.value_2_desc", "Hingga 600 km sekali charging."),
          },
          {
            iconKey: t("home.value_3_icon", "zap"),
            iconUrl: t("home.value_3_icon_url", ""),
            title: t("home.value_3_title", "Hemat Biaya Operasional"),
            desc: t("home.value_3_desc", "Biaya listrik hanya Rp 150-200/km."),
          },
        ].map(({ iconKey, iconUrl, title, desc }) => {
          const IconComp = VALUE_ICON_MAP[iconKey] || BatteryCharging;
          return (
            <div key={title} className="flex gap-5 items-start">
              <div className="shrink-0 w-12 h-12 rounded-2xl bg-[#0F0F0F] text-white flex items-center justify-center overflow-hidden">
                {iconUrl ? (
                  <img
                    src={iconUrl}
                    alt={title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <IconComp className="w-5 h-5" strokeWidth={1.5} />
                )}
              </div>
              <div>
                <h3 className="text-lg font-medium text-[#0F0F0F]">{title}</h3>
                <p className="mt-2 text-sm text-[#6E6E73] leading-relaxed">
                  {desc}
                </p>
              </div>
            </div>
          );
        })}
      </section>

      {/* All model showcase grid */}
      <section
        className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32"
        data-testid="all-models-section"
      >
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#6E6E73] mb-5">
              {t("home.lineup_eyebrow", "Lineup Lengkap")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F0F0F] leading-[1.1] whitespace-pre-line">
              {t("home.lineup_heading", "Tujuh model.\nSatu dealer resmi.")}
            </h2>
          </div>
          <Link
            to="/models"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#0F0F0F] hover:underline underline-offset-4"
          >
            Lihat semua model
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {others.map((m, i) => (
            <Link
              key={m.slug}
              to={`/model/${m.slug}`}
              className={`group rounded-3xl border border-[#E5E5EA] bg-[#F5F5F7] overflow-hidden hover:border-[#0F0F0F]/30 transition-colors ${
                i === 0 ? "sm:col-span-2 lg:col-span-1" : ""
              }`}
              data-testid={`model-card-${m.slug}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#F5F5F7] relative">
                {loaded ? (
                  <Picture
                    key={m.thumb}
                    src={m.thumb}
                    alt={m.name}
                    loading="lazy"
                    width={600}
                    height={450}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[#EAEAEC] animate-pulse" />
                )}
              </div>
              <div className="p-6">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#86868B]">
                  {m.category}
                </p>
                <div className="mt-2 flex items-start justify-between gap-3">
                  <h3 className="text-xl font-medium text-[#0F0F0F]">
                    {m.name}
                  </h3>
                  <ArrowUpRight className="w-4 h-4 text-[#0F0F0F] shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
                <p className="text-sm text-[#6E6E73] mt-1">{m.tagline}</p>
                <div className="mt-5 flex items-center text-sm pt-4 border-t border-[#E5E5EA]">
                  <span className="text-[#0F0F0F] font-medium">
                    {m.priceLabel}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <SalesIdentity />
      <Handovers />
      <FAQ
        title={t("faq.title", "Pertanyaan yang Sering Diajukan")}
        subtitle={t("faq.subtitle", "")}
      />
      <DealerLocation />

      {/* Final CTA */}
      <section className="bg-[#0F0F0F] text-white" data-testid="final-cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-white/60 mb-5">
            {t("home.final_eyebrow", "Langkah Berikutnya")}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-3xl mx-auto whitespace-pre-line">
            {t("home.final_heading", "Siap jadwalkan\ntest drive?")}
          </h2>
          <p className="mt-6 text-base text-white/70 max-w-xl mx-auto">
            {t("home.final_desc", "Hubungi tim kami langsung untuk jadwal test drive.")}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={buildWaLink(settings)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppLead("final-cta")}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white px-7 py-4 text-sm font-medium transition-colors"
              data-testid="final-cta-whatsapp"
            >
              {settings.wa_cta_final || settings.wa_cta_text}
            </a>
            <Link
              to="/models"
              className="inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white px-7 py-4 text-sm font-medium border border-white/20 transition-colors"
            >
              Jelajahi Semua Model
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
